import { Component, inject, signal } from '@angular/core';
import {
	FormControl,
	FormGroup,
	FormsModule,
	ReactiveFormsModule,
} from '@angular/forms';
import { Router } from '@angular/router';
import {
	DestroyService,
	DialogBase,
	DialogOptions,
} from '@context/frontend/common';
import { GlobalSearchService } from '@context/frontend/search';
import { TablePlaceholderComponent } from '@context/frontend/table';
import { AlgoliaGlobal } from '@context/shared/types/search';
import { TranslocoModule } from '@jsverse/transloco';
import { TuiLoader, TuiInput } from '@taiga-ui/core';
import { TuiSearch } from '@taiga-ui/layout';
import { debounceTime, takeUntil, tap } from 'rxjs';
import { GlobalSearchChemicalComponent } from '../global-search-chemical/global-search-chemical.component';
import { GlobalSearchFileComponent } from '../global-search-file/global-search-file.component';
import { GlobalSearchFolderComponent } from '../global-search-folder/global-search-folder.component';
import { GlobalSearchMediaComponent } from '../global-search-media/global-search-media.component';
import { GlobalSearchUserComponent } from '../global-search-user/global-search-user.component';

@Component({
	imports: [
		TuiLoader,
		TranslocoModule,
		GlobalSearchUserComponent,
		GlobalSearchMediaComponent,
		GlobalSearchFolderComponent,
		GlobalSearchFileComponent,
		GlobalSearchChemicalComponent,
		TablePlaceholderComponent,
		TuiSearch,
		TuiInput,
		FormsModule,
		ReactiveFormsModule,
	],
	selector: 'ctx-global-search-dialog',
	templateUrl: 'global-search-dialog.component.html',
	providers: [DestroyService],
})
export class GlobalSearchDialogComponent extends DialogBase {
	static override readonly DialogOptions = {
		label: 'action.search',
		appearance: 'fullscreen',
	} as DialogOptions;

	readonly destroy$ = inject(DestroyService);
	readonly router = inject(Router);
	readonly searchService = inject(GlobalSearchService);

	readonly search = new FormGroup({
		term: new FormControl(''),
	});

	// `metadata` is accessed per discriminated `type` in the template, so the
	// generic must stay `any` to keep the flexible template access compiling.
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	readonly data = signal<AlgoliaGlobal<any>[]>([]);
	readonly totalElements = signal(0);

	readonly loading = signal(false);

	constructor() {
		super();
		this.initialize();
	}

	initialize() {
		this.search
			.get('term')
			?.valueChanges.pipe(
				takeUntil(this.destroy$),
				tap(() => {
					this.loading.set(true);
				}),
				debounceTime(500),
			)
			.subscribe((term) => {
				if (!term) {
					this.data.set([]);
					this.loading.set(false);
					return;
				}

				this.fetchData(term);
			});
	}

	fetchData(term: string) {
		this.loading.set(true);
		this.searchService
			.fetch(term)
			.then((res) => {
				this.totalElements.set(res.nbHits ?? 0);
				this.data.set(res.hits ?? []);
			})
			.finally(() => {
				this.loading.set(false);
			});
	}

	onSelect(path: string[]) {
		this.router.navigate(path);
		this.onDismiss();
	}
}
